import { Controller, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { ReactNode, useState } from "react";
import Button from "./Button";
import MarkdownEditor from "./MarkdownEditor";
import FileUploadPlugin from "./MarkdownEditor/plugins/FileUpload";
import SuggestionPlugin from "./MarkdownEditor/plugins/Suggestion";

type FormData = {
  content: string;
};

interface SubmitData {
  content: string;
  setFormError: React.Dispatch<React.SetStateAction<string | undefined>>;
  resetForm: () => void;
}

interface Props {
  id?: ID;
  namespaceId?: ID;
  content?: string;
  onSubmit: (data: SubmitData) => void;
  onCancel?: () => void;
  isDisabled?: boolean;
  autoFocus?: boolean;
  submitLabel: ReactNode;
}

export default function CommentEditForm({
  id,
  namespaceId,
  onSubmit,
  onCancel,
  isDisabled = false,
  autoFocus,
  content,
  submitLabel,
}: Props) {
  const intl = useIntl();
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, dirtyFields },
  } = useForm<FormData>({ defaultValues: { content } });
  const [formError, setFormError] = useState<string | undefined>(undefined);

  const resetForm = () => {
    reset();
    setFormError(undefined);
  };
  const errorMessages = {
    required: intl.formatMessage({
      defaultMessage: "Comment is required",
    }),
    maxLength: intl.formatMessage({
      defaultMessage: "Comment cannot be longer than 1000 characters",
    }),
  };
  const doSubmit = handleSubmit(({ content }: FormData) => {
    if (content && !dirtyFields.content) {
      if (onCancel) {
        onCancel();
      }
      return;
    }
    setFormError(undefined);
    onSubmit({ content: content.trim(), setFormError, resetForm });
  });
  const error =
    formError ||
    (typeof errors.content?.type === "string" &&
      errorMessages[errors.content.type as keyof typeof errorMessages]);

  return (
    <form onSubmit={doSubmit}>
      <Controller
        name="content"
        control={control}
        rules={{
          required: true,
          maxLength: 2000,
        }}
        render={({ field }) => (
          <MarkdownEditor
            {...field}
            namespaceId={namespaceId}
            autoFocus={autoFocus}
          >
            <FileUploadPlugin />
            <SuggestionPlugin source={{ member: id }} />
          </MarkdownEditor>
        )}
      />
      {error && <p className="text-red-500"> {error}</p>}
      <div className="flex flex-row space-x-4 py-2 justify-end">
        {onCancel && (
          <Button kind="text" disabled={isDisabled} onClick={onCancel}>
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
        )}
        <Button kind="primary" disabled={isDisabled} onClick={doSubmit}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
